// Client for the partner marketplace endpoints of the lawyer backend
// (plan/marketplace/contract.md, section 4). Partner endpoints serve firm users
// (is_marketplace_partner); admin endpoints serve in-house lawyers with
// marketplaceAdmin (is_marketplace_admin).

import { request } from './client';

export type ServiceType =
  | 'visa'
  | 'residence_permit'
  | 'citizenship'
  | 'bank_account'
  | 'company_formation'
  | 'other';

export type Urgency = 'low' | 'normal' | 'high';

export type RequestStatus =
  | 'DETECTED'
  | 'RESERVED'
  | 'CLAIMED_INHOUSE'
  | 'RELEASED'
  | 'OPEN'
  | 'AWAITING_CUSTOMER'
  | 'AWARDED'
  | 'COMPLETED'
  | 'DISMISSED'
  | 'EXPIRED'
  | 'CANCELLED';

export type PriceType = 'fixed' | 'from';
export type BidStatus = 'submitted' | 'withdrawn' | 'won' | 'lost';
export type FirmType = 'law_firm' | 'accountant' | 'agency';
export type MarketplaceUserRole = 'owner' | 'member';

// Timestamps come back from the backend as ISO strings. Older serialisations
// of Firestore timestamps ({seconds} / {_seconds}) are accepted too; see
// toDate in the marketplace components.
export type Timestamp = string | number | { seconds?: number; _seconds?: number } | null;

export interface Bid {
  firmId: string;
  firmName: string;
  priceCents: number;
  currency: 'EUR';
  priceType: PriceType;
  etaDays: number;
  message: string;
  status: BidStatus;
  submittedBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface PartnerRequest {
  id: string;
  status: RequestStatus;
  serviceType: ServiceType;
  countries: string[];
  language: string;
  urgency: Urgency;
  anonymisedSummary: string;
  bidDeadline: Timestamp;
  createdAt: Timestamp;
  myBid: Bid | null;
  // Only present for the firm that won the award.
  customer?: { name: string | null; email: string | null; language: string | null };
}

export interface ClassifierInfo {
  isMarketplace?: boolean;
  confidence?: number;
  reason?: string;
  model?: string;
}

export interface AdminRequest {
  id: string;
  status: RequestStatus;
  serviceType: ServiceType;
  countries: string[];
  language: string;
  urgency: Urgency;
  anonymisedSummary: string;
  source: 'email' | 'chat' | 'manual';
  sourceRef?: { ticketId?: string; messageId?: string; inboundEmailId?: string } | null;
  userId: string | null;
  customerEmail: string | null;
  customerName: string | null;
  classifier: ClassifierInfo | null;
  reservedUntil?: Timestamp;
  claimedBy?: string | null;
  claimedAt?: Timestamp;
  releasedAt?: Timestamp;
  consentAt?: Timestamp;
  matchingFee?: {
    required?: boolean;
    amountCents?: number;
    currency?: string;
    paidAt?: Timestamp;
    refundedAt?: Timestamp;
  } | null;
  bidDeadline?: Timestamp;
  requestedAt?: Timestamp;
  bidCount?: number;
  awaitingCustomerSince?: Timestamp;
  awardedFirmId?: string | null;
  awardedAt?: Timestamp;
  completedAt?: Timestamp;
  dismissReason?: string | null;
  cancelReason?: string | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface RequestEvent {
  id?: string;
  type: string;
  from: RequestStatus | null;
  to: RequestStatus;
  actorUid?: string | null;
  actorRole: 'system' | 'admin' | 'partner' | 'customer';
  note?: string | null;
  at: Timestamp;
}

export interface Firm {
  id: string;
  name: string;
  type: FirmType;
  services: ServiceType[];
  languages: string[];
  oaNumber?: string | null;
  contactEmail: string;
  agreementSignedAt?: Timestamp;
  dpaSignedAt?: Timestamp;
  active: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface MarketplaceUser {
  id: string;
  email: string;
  firmId: string;
  role: MarketplaceUserRole;
  uid: string | null;
  active: boolean;
  invitedBy?: string;
  createdAt?: Timestamp;
}

export type FirmWithUsers = Firm & { users: MarketplaceUser[] };

export interface BidInput {
  priceCents: number;
  priceType: PriceType;
  etaDays: number;
  message: string;
}

export interface CreateRequestInput {
  ticketId?: string;
  userId?: string;
  customerEmail?: string;
  customerName?: string;
  serviceType: ServiceType;
  countries: string[];
  language: string;
  urgency?: Urgency;
  anonymisedSummary: string;
}

export type RequestPatch = Partial<
  Pick<
    AdminRequest,
    | 'serviceType'
    | 'countries'
    | 'language'
    | 'urgency'
    | 'anonymisedSummary'
    | 'userId'
    | 'customerEmail'
    | 'customerName'
  >
>;

export interface FirmInput {
  name: string;
  type: FirmType;
  services: ServiceType[];
  languages: string[];
  oaNumber?: string;
  contactEmail: string;
  agreementSignedAt?: string;
  dpaSignedAt?: string;
}

const json = (method: string, body?: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };

// Partner endpoints

export const getMarketplaceMe = async (
  token: string
): Promise<{ user: MarketplaceUser; firm: Firm }> =>
  request(token, '/marketplace/me', json('GET'), 'Failed to load your firm.');

export const listPartnerRequests = async (token: string): Promise<PartnerRequest[]> => {
  const data = await request(token, '/marketplace/requests', json('GET'), 'Failed to load open requests.');
  return data?.requests || [];
};

export const getPartnerRequest = async (token: string, id: string): Promise<PartnerRequest> =>
  request(token, `/marketplace/requests/${id}`, json('GET'), 'Failed to load the request.');

export const putBid = async (token: string, id: string, bid: BidInput): Promise<any> =>
  request(token, `/marketplace/requests/${id}/bid`, json('PUT', bid), 'Failed to save your bid.');

export const withdrawBid = async (token: string, id: string): Promise<any> =>
  request(token, `/marketplace/requests/${id}/bid`, json('DELETE'), 'Failed to withdraw your bid.');

export const completeRequest = async (token: string, id: string): Promise<any> =>
  request(token, `/marketplace/requests/${id}/complete`, json('POST'), 'Failed to mark the request completed.');

export const listMyWork = async (
  token: string
): Promise<{ request: PartnerRequest; bid: Bid }[]> => {
  const data = await request(token, '/marketplace/my-work', json('GET'), 'Failed to load your work.');
  return data?.items || [];
};

// Admin endpoints

export const listAdminRequests = async (
  token: string,
  status: RequestStatus
): Promise<AdminRequest[]> => {
  const data = await request(
    token,
    `/marketplace/admin/requests?status=${encodeURIComponent(status)}`,
    json('GET'),
    'Failed to load marketplace requests.'
  );
  return data?.requests || [];
};

export const getAdminRequest = async (
  token: string,
  id: string
): Promise<{ request: AdminRequest; bids: Bid[]; events: RequestEvent[] }> => {
  const data = await request(
    token,
    `/marketplace/admin/requests/${id}`,
    json('GET'),
    'Failed to load the request.'
  );
  return { request: data?.request, bids: data?.bids || [], events: data?.events || [] };
};

export const createAdminRequest = async (token: string, input: CreateRequestInput): Promise<any> =>
  request(token, '/marketplace/admin/requests', json('POST', input), 'Failed to create the request.');

export const patchAdminRequest = async (token: string, id: string, patch: RequestPatch): Promise<any> =>
  request(token, `/marketplace/admin/requests/${id}`, json('PATCH', patch), 'Failed to save the request.');

export const confirmRequest = async (token: string, id: string, reserveHours?: number): Promise<any> =>
  request(
    token,
    `/marketplace/admin/requests/${id}/confirm`,
    json('POST', reserveHours ? { reserveHours } : {}),
    'Failed to confirm the request.'
  );

export const claimRequest = async (token: string, id: string): Promise<any> =>
  request(token, `/marketplace/admin/requests/${id}/claim`, json('POST'), 'Failed to claim the request.');

export const releaseRequest = async (token: string, id: string): Promise<any> =>
  request(token, `/marketplace/admin/requests/${id}/release`, json('POST'), 'Failed to release the request.');

export const dismissRequest = async (token: string, id: string, reason: string): Promise<any> =>
  request(
    token,
    `/marketplace/admin/requests/${id}/dismiss`,
    json('POST', { reason }),
    'Failed to dismiss the request.'
  );

export const listFirms = async (token: string): Promise<FirmWithUsers[]> => {
  const data = await request(token, '/marketplace/admin/firms', json('GET'), 'Failed to load firms.');
  return data?.firms || [];
};

export const createFirm = async (token: string, input: FirmInput): Promise<any> =>
  request(token, '/marketplace/admin/firms', json('POST', input), 'Failed to create the firm.');

export const patchFirm = async (
  token: string,
  firmId: string,
  patch: Partial<FirmInput> & { active?: boolean }
): Promise<any> =>
  request(token, `/marketplace/admin/firms/${firmId}`, json('PATCH', patch), 'Failed to save the firm.');

export const inviteFirmUser = async (
  token: string,
  firmId: string,
  email: string,
  role: MarketplaceUserRole
): Promise<any> =>
  request(
    token,
    `/marketplace/admin/firms/${firmId}/users`,
    json('POST', { email, role }),
    'Failed to invite the user.'
  );

export const deactivateFirmUser = async (token: string, firmId: string, userId: string): Promise<any> =>
  request(
    token,
    `/marketplace/admin/firms/${firmId}/users/${userId}`,
    json('DELETE'),
    'Failed to deactivate the user.'
  );
